import { JstDate } from '../../core/time/business-time';
import { Component, OnInit, inject } from '@angular/core';

import { RouterLink } from '@angular/router';
import { PageTitleService } from '../../core/services/page-title.service';
import { MemberApiService } from '../../core/services/member-api.service';
import { environment } from '../../../environments/environment';
import Chart from 'chart.js';

@Component({
    selector: 'app-counsel',
    imports: [RouterLink],
    templateUrl: './counsel.component.html'
})
export class CounselComponent implements OnInit {
  private pageTitle = inject(PageTitleService);
  private memberApi = inject(MemberApiService);

  items: any[] = [];
  filter: any[] = [];
  pageLimit = 100;
  limitBegin = 0;
  searching = true;
  showTable = false;
  keyword = '';
  select: any = {};
  // 旧版 mypage/js/controllers/counsel.js:231-241 の getLineGraphOptions()
  lineGraphOptions = [
    {id: 1, name: '体重の推移', label: '体重（kg）', column: 'weight'},
    {id: 2, name: '体脂肪率の推移', label: '体脂肪率（％）', column: 'bodyFatPercentage'},
    {id: 3, name: '筋肉量の推移', label: '筋肉量（kg）', column: 'muscleMass'},
    {id: 4, name: '太もも周りの推移', label: '太もも周り（cm）', column: 'thighCircumference'},
    {id: 5, name: 'お尻周りの推移', label: 'お尻周り（cm）', column: 'hipCircumference'},
    {id: 6, name: 'お腹周りの推移', label: 'お腹周り（cm）', column: 'abdominalCircumference'},
    {id: 7, name: '二の腕周りの推移', label: '二の腕周り（cm）', column: 'armCircumference'},
  ];
  private graph1: any = null;
  private graph2: any = null;

  async ngOnInit(): Promise<void> {
    this.pageTitle.set('fa s-icon-list', 'カウンセリング');
    const data = await this.memberApi.getCounsel('self');
    this.searching = false;
    this.items = Array.isArray(data) ? data : [];
    this.showTable = this.items.length > 0;
    this.applyFilter();
  }

  applyFilter(): void {
    this.filter = this.items.filter(item =>
      !this.keyword || JSON.stringify(item).toLowerCase().includes(this.keyword.toLowerCase())
    );
  }

  get pagerCount(): number[] {
    return Array.from({ length: Math.ceil(this.filter.length / this.pageLimit) }, (_, i) => i);
  }

  get pagedItems(): any[] {
    return this.filter.slice(this.limitBegin, this.limitBegin + this.pageLimit);
  }

  pagerClick(index: number): void {
    this.limitBegin = index * this.pageLimit;
  }

  updateGraph(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.select.lineGraph = value ? this.lineGraphOptions.find(l => l.id === +value) : null;
    // 旧版 counsel.js:66-83 と同じく選択に応じて折れ線グラフを描き直す
    if (this.graph1) { this.graph1.destroy(); this.graph1 = null; }
    if (this.graph2) { this.graph2.destroy(); this.graph2 = null; }
    if (!this.select.lineGraph) return;
    // canvas は @if (select.lineGraph) の配下にあるため描画は次のマクロタスクで行う
    setTimeout(() => this.renderGraphs(), 0);
  }

  private renderGraphs(): void {
    this.graph1 = this.renderGraph('myChart', '.canvas-container');
    this.graph2 = this.renderGraph('myChartModal', '.canvas-container-modal');
  }

  private renderGraph(canvasId: string, containerSelector: string): any {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement | null;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return null;
    ctx.canvas.height = 300;
    const width = document.querySelector(containerSelector)?.clientWidth;
    if (width) ctx.canvas.width = width;
    return new Chart(ctx, this.createGraphData() as any);
  }

  private createGraphData(): any {
    const column = this.select.lineGraph?.column;
    let suggestedMin = 0;
    let suggestedMax = 0;
    if (this.items.length > 0) {
      const graphValues = this.items
        .map(v => parseInt(v[column], 10))
        .filter(v => !isNaN(v));
      if (graphValues.length > 0) {
        suggestedMin = Math.min(...graphValues) - 5;
        suggestedMax = Math.max(...graphValues) + 5;
      }
    }
    return {
      type: 'line',
      data: {
        labels: this.getGraphLabels(),
        datasets: [{
          label: this.select.lineGraph ? this.select.lineGraph.label : null,
          data: this.getGraphData(),
          borderColor: 'rgba(230,26,123,1)',
          backgroundColor: 'rgba(230,26,123,1)',
          lineTension: 0,
          fill: false,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
            type: 'linear',
            ticks: {
              suggestedMin,
              suggestedMax,
              stepSize: parseInt(String((suggestedMax - suggestedMin) / 7), 10),
            },
          }],
        },
      },
    };
  }

  private getGraphLabels(): string[] {
    let labels: string[] = [];
    if (this.items.length > 0) {
      labels = this.items.map(row => this.formatDate(row['counseledAt']));
      labels.reverse();
    }
    // 旧版と同じく14目盛りになるよう空ラベルで埋める
    const fill = Array(Math.max(0, 14 - labels.length)).fill(' ');
    return labels.concat(fill);
  }

  private getGraphData(): any[] {
    const column = this.select.lineGraph?.column;
    if (this.items.length === 0) return [];
    return this.items.map(row => row[column]).reverse();
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    if (dateStr.endsWith('Z')) {
      const d = new JstDate(dateStr);
      return `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}`;
    }
    return dateStr.split('T')[0];
  }
}
